import { SliceIterator } from '@/ts/util/iterators';


export function removeAllFromString(string: string, ...removeValues: string[]): string {
    let strippedString = string;
    for (const removeValue of removeValues) {
        strippedString = strippedString.replaceAll(removeValue, '');
    }
    return strippedString;
}

export function clamp(lower: number, mainNum: number, upper: number) {
    return Math.min(Math.max(mainNum, lower), upper);
}

export function roundNumber(number: number, places: number = 0): number {
    return Number(number.toFixed(places));
}

export function getHighest(values: number[] | Iterable<number>): number {
    const numbers = Array.from(values);
    if (numbers.length === 0)
        return Number.MAX_VALUE;
    return numbers.reduce((highest, value) => value > highest ? value : highest);
}

export function getLowest(values: number[] | Iterable<number>): number {
    const numbers = Array.from(values);
    if (numbers.length === 0)
        return Number.MIN_VALUE;
    return numbers.reduce((lowest, value) => value < lowest ? value : lowest);
}

export function getHighestMultiple(values: number[] | Iterable<number>, amount: number): number[] {
    const numbers = Array.from(values);
    numbers.sort((a, b) => b - a);
    return numbers.filter((_, i) => i < amount);
}

export function getLowestMultiple(values: number[] | Iterable<number>, amount: number): number[] {
    const numbers = Array.from(values);
    numbers.sort((a, b) => a - b);
    return numbers.filter((_, i) => i < amount);
}

export function getSum(values: number[] | Iterable<number>) {
    const numbers = Array.from(values);
    if (numbers.length === 0)
        return 0;
    return numbers.reduce((sum, value) => sum + value);
}

export function getAverage(values: number[] | Iterable<number>) {
    const numbers = Array.from(values);
    if (numbers.length === 0)
        return 0;
    return getSum(numbers) / numbers.length;
}

export function getMovingAverage(data: number[], period: number) {
    const periodSegments = Array.from(SliceIterator.from(data, period));
    return periodSegments.map(getAverage);
}

export function getStandardDeviation(values: number[] | Iterable<number>) {
    const numbers = Array.from(values);
    if (numbers.length === 0)
        return 0;

    const average = getAverage(numbers);
    const distanceFromAverage = numbers.map(value => value - average);
    const squaredDistance = distanceFromAverage.map(distance => Math.pow(distance, 2));
    const squaredAverage = getAverage(squaredDistance);
    const standardDeviation = Math.sqrt(squaredAverage);

    return standardDeviation;
}

export function getRangeWithPadding(values: number[] | Iterable<number>, countPercentage: number = 0.03): [lower: number, upper: number]  {
    const numbers = Array.from(values);
    const MIN_ELEMENT_AMOUNT = 2;
    const elementCount = Math.max(Math.floor(numbers.length * countPercentage), MIN_ELEMENT_AMOUNT);

    const lowestElements = getLowestMultiple(values, elementCount);
    const highestElements = getHighestMultiple(values, elementCount);
    const highestOfLowest = getHighest(lowestElements);
    const lowestOfHighest = getLowest(highestElements);

    const rangeBound = highestOfLowest > Math.abs(lowestOfHighest) ? highestOfLowest : Math.abs(lowestOfHighest);
    return [-rangeBound, rangeBound];
}

export function getRangeStepSpan(range: [a: number, b: number], amountOfSteps: number) {
    const [lower, upper] = range;
    const array: number[] = [];

    const stepValue = (upper - lower) / (amountOfSteps - 1); // from slope formula (-1 is to make upper range inclusive) ->   P(0, lower)   &   P(amountOfSteps - 1, upper)

    let currentCounter = lower;
    for (let i = 0; i < amountOfSteps; i++) {
        array[i] = currentCounter;
        currentCounter += stepValue;
    }

    return array;
}

export function parseHex(num: string): number {
    return parseInt(num, 16);
}

export function spliceString(mainString: string, spliceString: string, index: number) {
    return mainString.substring(0, index) + spliceString + mainString.substring(index, mainString.length);
}

export function spliceStringAtIndexes(mainString: string, stringToSplice: string, ...indexes: number[]) {
    let formattedString = mainString;
    for (const index of indexes) {
        formattedString = spliceString(formattedString, stringToSplice, index);
    }
    return formattedString;
}

export function isInteger(value: number, tolerance: number = 0) {
    const actualInteger = Math.round(value);
    return value >= actualInteger - tolerance && value <= actualInteger + tolerance;
}

export function min<T>(array: T[], callback: (entry: T) => number) {
    let min: T = array[0];
    for (const element of array) {
        if (callback(element) < callback(min))
            min = element;
    }
    return min;
}

export function max<T>(array: T[], callback: (entry: T) => number) {
    let max: T = array[0];
    for (const element of array) {
        if (callback(element) > callback(max))
            max = element;
    }
    return max;
}

interface ArrayEntry<T> {
    value: T;
    index: number;
}

export function findMin(array: number[], start: number = 0, end: number = array.length): ArrayEntry<number> {
    const entries = Array.from(array.slice(start, end).entries());
    const minValueEntry = min(entries, entry => entry[1]);

    return {
        value: minValueEntry[1],
        index: minValueEntry[0] + start
    };
}

export function findMax(array: number[], start: number = 0, end: number = array.length): ArrayEntry<number> {
    const entries = Array.from(array.slice(start, end).entries());
    const minValueEntry = max(entries, entry => entry[1]);

    return {
        value: minValueEntry[1],
        index: minValueEntry[0] + start
    };
}